import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Button, Grid } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import axios from 'axios';

const Clientes = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear(); // Obtém o ano corrente

  const fetchColaboradorNome = async (id) => {
    try {
      const response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/colaboradores/${id}`);
      return response.data.nome;
    } catch (error) {
      console.error(`Erro ao buscar colaborador com id ${id}:`, error);
      return null;
    }
  };

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/clientes`);
        const clientesData = response.data;

        const clientesComInfoAdicional = await Promise.all(clientesData.map(async (cliente) => {
          const aprovador1Nome = await fetchColaboradorNome(cliente.aprovador1);
          const aprovador2Nome = await fetchColaboradorNome(cliente.aprovador2);

          return {
            ...cliente,
            aprovador1Nome: aprovador1Nome || cliente.aprovador1,
            aprovador2Nome: aprovador2Nome || cliente.aprovador2,
          };
        }));

        setClientes(clientesComInfoAdicional);
        console.log("Clientes carregados:", clientesComInfoAdicional);
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientes(); // Chamada imediata ao montar o componente

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // <-- Array de dependências vazio significa que o useEffect será executado apenas uma vez, sem depender de variáveis externas

  const handleEdit = (id) => {
    navigate(`/clientes/editar/${id}`);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm("Tem certeza de que deseja excluir este cliente?")) {
      try {
        await axios.delete(`https://pf-auditores.ew.r.appspot.com/api/clientes/${id}`);
        const updatedClientes = clientes.filter(cliente => cliente.id !== id);
        setClientes(updatedClientes);
        console.log("Cliente removido com sucesso!");
      } catch (error) {
        console.error("Erro ao remover cliente:", error);
      }
    }
  };

  const columns = [
    {
      field: "numCliente",
      headerName: "Número",
      flex: 0.4,
      cellClassName: "name-column--cell",
      renderCell: (params) => {
        return (
          <Typography
            variant="body1"
            onClick={() => handleEdit(params.row.id)}
            style={{ cursor: "pointer", color: colors.greenAccent[300] }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "grupoI",
      headerName: "Grupo I",
      flex: 0.5,
    },
    {
      field: "grupoII",
      headerName: "Grupo II",
      flex: 0.5,
    },
    {
      field: "nif",
      headerName: "NIF",
      flex: 0.5,
    },
    {
      field: "nomeCliente",
      headerName: "Nome do Cliente",
      flex: 1,
    },
    {
      field: "vinculo",
      headerName: "Vínculo",
      flex: 0.5,
    },
    {
      field: "periodo",
      headerName: "Período",
      flex: 0.5,
    },
    {
      field: "honorarios",
      headerName: "Honorários",
      flex: 0.5,
      renderCell: (params) => {
        const euroFormatter = new Intl.NumberFormat("pt-PT", {
          style: "currency",
          currency: "EUR",
        });
        const mandatos = params.row.mandatos;
        const honorarioAtual = mandatos.find(mandato => mandato.ano === currentYear.toString());
        const ultimoHonorario = mandatos[mandatos.length - 1];
        
        return (
          <Typography variant="body2">
            {honorarioAtual ? euroFormatter.format(honorarioAtual.honorario) : (ultimoHonorario ? euroFormatter.format(ultimoHonorario.honorario) : "N/A")}
          </Typography>
        );
      },
    },
    {
      field: "socio",
      headerName: "Sócio",
      flex: 0.5,
    },
    {
      field: "aprovador1Nome",
      headerName: "Aprovador 1",
      flex: 0.5,
      renderCell: (params) => {
        const nomeAbreviado = params.value.split(' ');
        return (
          <Typography variant="body2">
            {nomeAbreviado[0]} {nomeAbreviado[nomeAbreviado.length - 1]}
          </Typography>
        );
      },
    },
    {
      field: "aprovador2Nome",
      headerName: "Aprovador 2",
      flex: 0.5,
      renderCell: (params) => {
        const nomeAbreviado = params.value.split(' ');
        return (
          <Typography variant="body2">
            {nomeAbreviado[0]} {nomeAbreviado[nomeAbreviado.length - 1]}
          </Typography>
        );
      },
    },
    {
      field: "edit",
      headerName: "",
      flex: 0.7,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEdit(params.row.id)}
          >
            Editar
          </Button>
        );
      },
    },
    {
      field: "delete",
      headerName: "",
      flex: 0.7,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteClick(params.row.id)}
          >
            Remover
          </Button>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <Header title="Clientes" subtitle="Lista de clientes registrados" />
      <Grid container justifyContent="space-between" marginBottom="20px">
        <Grid item>
          <Button variant="contained" color="primary" onClick={() => navigate('/clientes/registar')}>
            Adicionar Novo Cliente
          </Button>
        </Grid>
      </Grid>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={clientes}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default Clientes;
